import React, { useState, useContext, memo, useEffect } from "react";
import {
  useGetApplicationCountQuery,
  useGetVersionInfoQuery,
} from "../../services/stats";
import { useLocation, Link as RouterLink } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";

import {
  East,
  Menu as MenuIcon,
  Logout,
  VpnKey,
  Description as DescriptionIcon,
  AccountCircle,
} from "@mui/icons-material";

import { AuthContext } from "../../context/AuthContext";
import { NotificationContext } from "../../context/NotificationContext";

import nlsfLogo from "../../assets/icons/nlsf-scholarships.png";
import { DrawerContent, menuItems } from "./helpers";
import { formatPageTitle } from "../helpers/titleFormatter";
import ScholarshipBookDialog from "../helpers/ScholarshipBookDialog";

function Navbar() {
  // Quiet application count for all users
  const { data: appCountData, isLoading: appCountLoading } =
    useGetApplicationCountQuery();
  // Sum all application counts (API returns array)
  const totalApplications = Array.isArray(appCountData?.applicationCount)
    ? appCountData.applicationCount.reduce(
        (acc, item) => acc + (item.count || 0),
        0,
      )
    : 0;
  const { user, isAuthenticated, genAppLogoutAuthContext } =
    useContext(AuthContext);
  const { data: versionInfoData, isLoading: isVersionInfoLoading } =
    useGetVersionInfoQuery(undefined, { pollingInterval: 60_000 });
  const [versionInfoNow, setVersionInfoNow] = useState(Date.now());
  const [versionAnchorEl, setVersionAnchorEl] = useState(null);
  const { addNotification } = useContext(NotificationContext);

  useEffect(() => {
    const timer = setInterval(() => setVersionInfoNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const formatVersionAge = (updatedAt) => {
    const timestamp = updatedAt ? new Date(updatedAt).getTime() : NaN;
    if (!Number.isFinite(timestamp)) return "update time unavailable";
    const minutes = Math.max(0, Math.floor((versionInfoNow - timestamp) / 60_000));
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  };

  const frontendBuild = {
    version: process.env.REACT_APP_VERSION || "dev",
    updatedAt: process.env.REACT_APP_BUILD_TIME,
    gitHash: process.env.REACT_APP_GIT_HASH,
  };
  const rawBackendBuild = versionInfoData?.version?.backend;
  const backendBuild =
    typeof rawBackendBuild === "string"
      ? { version: rawBackendBuild }
      : rawBackendBuild;
  const versionTitle = (label, build) =>
    `${label}: ${build?.version || "unknown"}; updated ${
      build?.updatedAt ? new Date(build.updatedAt).toLocaleString() : "time unavailable"
    }${build?.gitHash ? `; commit ${build.gitHash}` : ""}`;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // Fix: Add missing accountAnchorEl state
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const accountMenuOpen = Boolean(accountAnchorEl);
  const userDisplayName = [
    user?.first_name || user?.firstName,
    user?.last_name || user?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const accountLabel = userDisplayName || user?.email || "Account";

  // Show only the current period (latest grad_year) count
  let currentPeriodCount = 0;
  if (
    Array.isArray(appCountData?.applicationCount) &&
    appCountData.applicationCount.length > 0
  ) {
    // Find the latest grad_year (assume sorted descending or get max)
    const latest = appCountData.applicationCount.reduce((a, b) =>
      Number(a.grad_year) > Number(b.grad_year) ? a : b,
    );
    currentPeriodCount = latest.count || 0;
  }
  const pathSegments = location.pathname.split("/").filter((seg) => seg);
  const rawPageTitle = pathSegments.length >= 1 ? pathSegments[0] : "Welcome";
  const formattedPageTitle = formatPageTitle(rawPageTitle);

  const handleLogout = () => {
    addNotification("You have successfully logged out.", "success");
    genAppLogoutAuthContext();
  };

  const handleDrawerToggle = () => setMobileOpen((p) => !p);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleOpenAccountMenu = (e) => setAccountAnchorEl(e.currentTarget);
  const handleCloseAccountMenu = () => setAccountAnchorEl(null);

  const combinedMenuItems = [
    ...menuItems,
    ...(isAuthenticated
      ? [
          {
            name: "Donate Today",
            icon: <East />,
            link: "https://www.canadahelps.org/en/dn/24199",
            external: true,
          },
          {
            name: "Change Password",
            icon: <VpnKey />,
            link: "/change-password",
          },
          { name: "Logout", icon: <Logout />, action: handleLogout },
        ]
      : []),
  ];

  // Desktop nav links (quiet)
  const navLinks = [
    { label: "Home", to: "/" },
    { label: "List of Awards", to: "/awards-page" },
    // {
    //   label: "Donor Selected Awards",
    //   href: "https://www.nlsf.ca/scholarships-bursaries/donor-selected-awards/",
    //   external: true,
    // },
    {
      label: "FAQs",
      href: "https://www.nlsf.ca/scholarships-bursaries/faq/",
      external: true,
    },
  ];

  const isActive = (to) => (to ? location.pathname === to : false);

  return (
    <Box className="navbar">
      <AppBar position="static" elevation={0} color="transparent">
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 1, sm: 2 },
          }}
        >
          {/* Left: Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ position: "relative", lineHeight: 0 }}>
              <Box
                component="img"
                src={nlsfLogo}
                alt="nlsf-logo"
                sx={{
                  width: { xs: 170, sm: 220, md: 250 },
                  maxWidth: "100%",
                }}
              />
              <Box
                component="button"
                type="button"
                aria-label="Show application versions"
                title="Show application versions"
                onClick={(event) => setVersionAnchorEl(event.currentTarget)}
                sx={{
                  position: "absolute",
                  left: "9%",
                  top: "29%",
                  width: "8%",
                  height: "20%",
                  p: 0,
                  border: 0,
                  borderRadius: "50%",
                  background: "transparent",
                  cursor: "pointer",
                }}
              />
            </Box>
            <Popover
              open={Boolean(versionAnchorEl)}
              anchorEl={versionAnchorEl}
              onClose={() => setVersionAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Stack spacing={0.5} sx={{ p: 1.5, minWidth: 220 }}>
                <Typography
                  variant="body2"
                  title={versionTitle("Frontend", frontendBuild)}
                >
                  FE v{frontendBuild.version} · {formatVersionAge(frontendBuild.updatedAt)}
                </Typography>
                <Typography
                  variant="body2"
                  title={versionTitle("Backend", backendBuild)}
                >
                  BE v{isVersionInfoLoading ? "…" : backendBuild?.version || "unknown"} ·{" "}
                  {formatVersionAge(backendBuild?.updatedAt)}
                </Typography>
              </Stack>
            </Popover>
          </Box>

          {/* Middle: Page title */}
          <Box sx={{ flex: 1, textAlign: "center" }}>
            {formattedPageTitle && (
              <Typography variant="h6" fontWeight={700}>
                {formattedPageTitle}
              </Typography>
            )}
          </Box>

          {/* Right: Desktop actions + nav (single row) */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Quiet nav links */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              {navLinks.map((item) => (
                <Button
                  key={item.label}
                  component={item.to ? RouterLink : "a"}
                  to={item.to}
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  variant="text"
                  size="small"
                  sx={{
                    color: "text.primary",
                    fontWeight: isActive(item.to) ? 800 : 600,
                    textDecoration: isActive(item.to) ? "underline" : "none",
                    textUnderlineOffset: "6px",
                    px: 1,
                    minWidth: "auto",
                  }}
                >
                  {item.label}
                </Button>
              ))}
              {/* Show only the number for the latest year, no label */}
              <Box
                sx={{
                  ml: 1,
                  opacity: 0.6,
                  fontSize: 8,
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                }}
                title={
                  appCountLoading
                    ? "Loading..."
                    : `Applications for ${
                        appCountData?.applicationCount?.[0]?.grad_year ||
                        "current period"
                      }`
                }
              >
                {appCountLoading ? "..." : currentPeriodCount}
              </Box>
            </Stack>

            <Divider orientation="vertical" flexItem />

            {/* Primary + secondary CTAs */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                onClick={handleOpenModal}
                startIcon={<DescriptionIcon />}
                size="xsmall"
                sx={{
                  color: "#FFF",
                  backgroundColor: "#5DC1AF",
                }}
              >
                Award Book
              </Button>

              <Button
                startIcon={<East />}
                variant="outlined"
                size="small"
                href="https://www.canadahelps.org/en/dn/24199"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderColor: "#5DC1AF",
                  color: "#1b3b36",
                }}
              >
                Donate
              </Button>

              {/* Account menu for auth-only actions */}
              {user && isAuthenticated && (
                <>
                  <Button
                    onClick={handleOpenAccountMenu}
                    size="small"
                    aria-label="Account menu"
                    startIcon={<AccountCircle />}
                    sx={{
                      color: "text.primary",
                      maxWidth: 190,
                      minWidth: 0,
                      textTransform: "none",
                      fontWeight: 700,
                      px: 0.75,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {accountLabel}
                    </Box>
                  </Button>

                  <Menu
                    anchorEl={accountAnchorEl}
                    open={accountMenuOpen}
                    onClose={handleCloseAccountMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem disabled>
                      <AccountCircle fontSize="small" style={{ marginRight: 8 }} />
                      {accountLabel}
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      component={RouterLink}
                      to="/change-password"
                      onClick={handleCloseAccountMenu}
                    >
                      <VpnKey fontSize="small" style={{ marginRight: 8 }} />
                      Change Password
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleCloseAccountMenu();
                        handleLogout();
                      }}
                    >
                      <Logout fontSize="small" style={{ marginRight: 8 }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Stack>
          </Box>

          {/* Mobile hamburger */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="end"
            onClick={handleDrawerToggle}
            className="mobile-menu-button"
            sx={{ display: { xs: "inline-flex", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        className="navbar-menu-drawer"
      >
        <Box sx={{ position: "relative", height: "100%" }}>
          <DrawerContent
            onClick={handleDrawerToggle}
            items={combinedMenuItems}
            onOpenModal={handleOpenModal}
          />
          {/* Mobile: Current period application count at bottom right */}
          <Box
            sx={{
              position: "absolute",
              right: 16,
              bottom: 12,
              opacity: 0.6,
              fontSize: 15,
              color: "text.secondary",
              zIndex: 2,
              pointerEvents: "none",
              fontWeight: 700,
            }}
            title={
              appCountLoading
                ? "Loading..."
                : `Applications for ${
                    appCountData?.applicationCount?.[0]?.grad_year ||
                    "current period"
                  }`
            }
          >
            {appCountLoading ? "..." : currentPeriodCount}
          </Box>
        </Box>
      </Drawer>

      <ScholarshipBookDialog
        open={modalOpen}
        onClose={handleCloseModal}
        addNotification={addNotification}
      />
    </Box>
  );
}

export default memo(Navbar);
